/**
 * LINE Login Button Component
 * Uses Cloudflare LINE LIFF Central Service SDK
 * In LINE Browser: Uses LIFF login directly
 * In external browser: Uses OAuth flow
 */

import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLineAuth, useLiff } from '@misterlex223/cloudflare-line-liff-sdk/react';
import { useLiffConfig } from '../../providers/LiffProvider';
import api from '../../services/api';
import './LineLoginButton.css';

interface LineLoginButtonProps {
  isInLineClient: boolean;
  redirectUrl?: string;
  mode?: 'login' | 'link';
  onLinkSuccess?: (profile: { displayName: string; pictureUrl?: string }) => void;
  onSuccess?: (result: any) => void;
  onError?: (error: Error) => void;
  isCallbackUrl?: boolean;
  onProcessingChange?: (processing: boolean) => void;
  className?: string;
}

const LineLoginButton: React.FC<LineLoginButtonProps> = ({
  isInLineClient,
  redirectUrl,
  mode = 'login',
  onLinkSuccess,
  onSuccess,
  onError,
  isCallbackUrl = false,
  onProcessingChange,
  className = '',
}) => {
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const config = useLiffConfig();

  // Initialize LIFF for LINE App login
  const liff = useLiff({
    ...config,
    onInitSuccess: () => {
      console.log('[LineLoginButton] LIFF initialized');
    },
    onInitError: (error) => {
      console.error('[LineLoginButton] LIFF init failed:', error);
    },
  });

  // Track if callback has been processed to avoid infinite loops
  const callbackProcessedRef = useRef(false);

  // Check if we've already attempted and failed to process this callback
  useEffect(() => {
    const state = searchParams.get('state');
    if (!state) return;

    const failedKey = `line_callback_failed_${state}`;
    const hasFailed = sessionStorage.getItem(failedKey);

    if (hasFailed && isCallbackUrl) {
      sessionStorage.removeItem(failedKey);
      const url = new URL(window.location.href);
      url.searchParams.delete('code');
      url.searchParams.delete('state');
      window.history.replaceState({}, '', url.toString());
    }
  }, [isCallbackUrl, searchParams]);

  // Initialize LINE Auth hook (for external browser)
  const lineAuth = useLineAuth({
    appId: config.appId,
    serviceUrl: config.serviceUrl,
    redirectUri: redirectUrl ? `${window.location.origin}${redirectUrl}` : `${window.location.origin}/login`,
    scope: 'profile openid email',
    onSuccess: async (result) => {
      setError(null);
      onProcessingChange?.(false);

      const state = searchParams.get('state');
      if (state) {
        sessionStorage.removeItem(`line_callback_failed_${state}`);
      }

      if (onSuccess) {
        await onSuccess(result);
      } else if (mode === 'link' && onLinkSuccess) {
        onLinkSuccess({
          displayName: result.displayName,
          pictureUrl: result.pictureUrl,
        });
      }
    },
    onError: (err) => {
      setError(err.message || 'LINE 登入失敗');
      onProcessingChange?.(false);

      const state = searchParams.get('state');
      if (state) {
        sessionStorage.setItem(`line_callback_failed_${state}`, 'true');
      }

      const url = new URL(window.location.href);
      url.searchParams.delete('code');
      url.searchParams.delete('state');
      window.history.replaceState({}, '', url.toString());

      onError?.(err);
    },
  });

  // Process callback automatically if this is a callback URL (external browser only)
  useEffect(() => {
    if (isCallbackUrl && !callbackProcessedRef.current && !isInLineClient) {
      callbackProcessedRef.current = true;

      const processCallback = async () => {
        onProcessingChange?.(true);
        setError(null);

        try {
          const currentUrl = new URL(window.location.href);
          const isCallback = lineAuth.isCallbackResponse(currentUrl);

          if (isCallback) {
            await lineAuth.handleCallback(currentUrl);
            const url = new URL(window.location.href);
            url.searchParams.delete('code');
            url.searchParams.delete('state');
            window.history.replaceState({}, '', url.toString());
          } else {
            onProcessingChange?.(false);
          }
        } catch (err) {
          const errorMsg = err instanceof Error ? err.message : 'Callback processing failed';
          setError(errorMsg);
          onProcessingChange?.(false);

          const state = searchParams.get('state');
          if (state) {
            sessionStorage.setItem(`line_callback_failed_${state}`, 'true');
          }
          const url = new URL(window.location.href);
          url.searchParams.delete('code');
          url.searchParams.delete('state');
          window.history.replaceState({}, '', url.toString());

          onError?.(err instanceof Error ? err : new Error(errorMsg));
        }
      };

      processCallback();
    }
  }, [isCallbackUrl, lineAuth, onProcessingChange, onError, searchParams, isInLineClient]);

  // Handle LIFF login (in LINE App)
  const handleLiffLogin = async () => {
    setError(null);
    onProcessingChange?.(true);

    try {
      // Wait for LIFF to be initialized
      if (!liff.isInitialized && !liff.isLoading) {
        await liff.init();
      }

      // Check if in LINE client
      if (!liff.isInClient()) {
        throw new Error('Not in LINE client');
      }

      // Check if logged in to LIFF
      if (!liff.isLoggedIn()) {
        // Not logged in, this shouldn't happen in normal flow
        // Redirect to /liff for proper LIFF initialization
        window.location.href = '/liff';
        return;
      }

      // Already logged in, get ID token and authenticate
      const idToken = liff.getIDToken();
      if (!idToken) {
        throw new Error('No ID token available');
      }

      const response = await api.post<any>('/api/auth/line/liff', { idToken });

      if (response.success && response.data) {
        const { token } = response.data;
        localStorage.setItem('auth_token', token);

        if (onSuccess) {
          await onSuccess({ lineUserId: response.data.lineUserId || '', ...response.data });
        } else {
          // Reload to update AuthContext
          window.location.reload();
        }
      } else {
        throw new Error(response.error?.message || 'Authentication failed');
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'LINE 登入失敗';
      setError(errorMsg);
      onProcessingChange?.(false);
      onError?.(err instanceof Error ? err : new Error(errorMsg));
    }
  };

  // Handle OAuth login (external browser)
  const handleOAuthLogin = async () => {
    setError(null);

    try {
      onProcessingChange?.(true);
      await lineAuth.login({
        state: crypto.randomUUID(),
      });
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'LINE 登入失敗';
      setError(errorMsg);
      onProcessingChange?.(false);
      onError?.(err instanceof Error ? err : new Error(errorMsg));
    }
  };

  const handleClick = async () => {
    // In LINE App, use LIFF login
    if (isInLineClient) {
      await handleLiffLogin();
    } else {
      // In external browser, use OAuth
      await handleOAuthLogin();
    }
  };

  return (
    <div className={`line-login-button ${className}`}>
      <button
        type="button"
        className="line-login-button__btn"
        onClick={handleClick}
        disabled={lineAuth.isLoading || isCallbackUrl}
      >
        {lineAuth.isLoading ? (
          <span className="line-login-button__loading">
            <span className="line-login-button__spinner"></span>
            處理中...
          </span>
        ) : (
          <>
            <svg
              className="line-login-button__icon"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 2C6.48 2 2 5.91 2 10.6C2 13.55 3.65 16.18 6.17 17.76C6.03 18.38 5.53 20.38 5.5 20.56C5.5 20.56 5.48 20.69 5.56 20.73C5.65 20.78 5.76 20.74 5.76 20.74C5.76 20.74 8.12 19.47 9.16 18.88C10.09 19.03 11.04 19.11 12 19.11C17.52 19.11 22 15.2 22 10.6C22 5.91 17.52 2 12 2Z"
                fill="currentColor"
              />
            </svg>
            <span className="line-login-button__text">
              {mode === 'link' ? '綁定 LINE 帳號' : '使用 LINE 登入'}
            </span>
          </>
        )}
      </button>
      {error && (
        <div className="line-login-button__error" role="alert">
          {error}
        </div>
      )}
    </div>
  );
};

export default LineLoginButton;
